import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ProcessData } from 'domain/shared/timeline';
import useEmployeesDashboardService from 'domain/people/employee/dashboard/useEmployeesDashboardService';
import { useShowContext } from 'framework/show';
import { EMPLOYEE_PROCESS_DEFINITION_PREFIXES } from '../constants';

interface ProcessesContextType {
  hrProcesses: ProcessData[];
  careerProcesses: ProcessData[];
  loading: boolean;
  error: string | null;
}

const ProcessesContext = createContext<ProcessesContextType | undefined>(undefined);

interface ProcessesProviderProps {
  children: ReactNode;
}

export const ProcessesProvider: React.FC<ProcessesProviderProps> = ({ children }) => {
  const { data } = useShowContext();
  const { getEmployeeProcesses } = useEmployeesDashboardService();
  const [hrProcesses, setHrProcesses] = useState<ProcessData[]>([]);
  const [careerProcesses, setCareerProcesses] = useState<ProcessData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProcesses = async () => {
      try {
        setLoading(true);
        setError(null);

        const employeeData = data.getData();
        const employeeAlias = employeeData?.alias;

        if (!employeeAlias) {
          throw new Error('Employee alias is required');
        }

        const processes = await getEmployeeProcesses(employeeAlias);

        const hrProcessesList = processes.filter((process: ProcessData) =>
          process.processDefinition.includes(EMPLOYEE_PROCESS_DEFINITION_PREFIXES.HR),
        );

        const careerProcessesList = processes.filter((process: ProcessData) =>
          process.processDefinition.includes(EMPLOYEE_PROCESS_DEFINITION_PREFIXES.CAREER_REVIEW),
        );

        setHrProcesses(hrProcessesList);
        setCareerProcesses(careerProcessesList);
      } finally {
        setLoading(false);
      }
    };

    fetchProcesses();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProcessesContext.Provider value={{ hrProcesses, careerProcesses, loading, error }}>
      {children}
    </ProcessesContext.Provider>
  );
};

export const useProcesses = (): ProcessesContextType => {
  const context = useContext(ProcessesContext);
  if (context === undefined) {
    throw new Error('useProcesses must be used within a ProcessesProvider');
  }
  return context;
};
